import { Play } from "lucide-react";
import { ProgressBar } from "@/components/ui/progress-bar";
import { cn } from "@/lib/utils";

export interface ModuleProgressInfo {
  title: string;
  chapterCount: number;
  progressPercent: number;
  status: "completed" | "in_progress" | "not_started";
  onClick?: () => void;
}

const STATUS_ICON_STYLES = {
  completed: "bg-[var(--success)] text-white",
  in_progress: "bg-[var(--primary)] text-white",
  not_started: "bg-[var(--muted)] text-[var(--muted-foreground)]",
} as const;

const STATUS_ICONS = {
  completed: <Play className="w-3.5 h-3.5" />,
  in_progress: <Play className="w-3.5 h-3.5" />,
  not_started: <Play className="w-3.5 h-3.5" />,
} as const;

const STATUS_LABELS = {
  completed: "abgeschlossen",
  in_progress: "in Bearbeitung",
  not_started: "noch nicht gestartet",
} as const;

const MODULE_BAR_COLORS = {
  completed: "success",
  in_progress: "secondary",
  not_started: "default",
} as const;

const renderPlayCircle = (
  status: ModuleProgressInfo["status"],
  isInteractive: boolean
) => {
  if (!isInteractive) return null;

  return (
    <span
      className={`flex items-center justify-center w-7 h-7 rounded-full shrink-0 ${STATUS_ICON_STYLES[status]}`}
    >
      {STATUS_ICONS[status]}
    </span>
  );
};

export const ModuleRow = ({ module }: { module: ModuleProgressInfo }) => {
  const isInteractive = typeof module.onClick === "function";

  return (
    <div
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onClick={module.onClick}
      onKeyDown={(e) => {
        if (!module.onClick) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          module.onClick();
        }
      }}
      className={cn(
        "flex items-center gap-3 px-3 py-2.5 rounded-lg",
        module.status === "in_progress" && "bg-accent",
        isInteractive && "cursor-pointer hover:bg-(--accent)/80"
      )}
    >
      {renderPlayCircle(module.status, isInteractive)}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-foreground truncate">
          {module.title}
        </div>
        <div className="text-xs text-muted-foreground">
          {module.chapterCount} Kapitel · {STATUS_LABELS[module.status]}
        </div>
      </div>
      <ProgressBar
        percentage={module.progressPercent}
        size="sm"
        color={MODULE_BAR_COLORS[module.status]}
        className="w-32 shrink-0"
      />
    </div>
  );
};
